/** @prettier */

import {Grid, Box} from '@mui/material';

//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack, iconWhite} from '../../define';
import {useTranslation} from 'react-i18next';

/**ActionがControl時のAction欄のアイコン表示
 * @param props props
 * - logicInfo ロジック単体の設定情報
 * @returns JSX
 */
export const LogicActionControlContents = (props: {
  logicInfo: apiIF.res.getSettingLogic['settings'][0];
}) => {
  const trans = useTranslation().t; //翻訳関数
  /**
   * 運転/停止アイコン
   * @param {apiIF.res.command['run-stop']} runStopState  対象ロジックの運転/停止状態
   * @returns アイコン画像
   */
  const runStopIcon = (runStopState: apiIF.res.command['run-stop']) => {
    switch (runStopState) {
      case 'run':
        return {
          src: iconWhite.AC_ON.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.AC_ON.alt}`
          ),
        };
      case 'stop':
        return {
          src: iconWhite.AC_OFF.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.AC_OFF.alt}`
          ),
        };
      default:
        return {
          src: iconBlack.MISSING.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconBlack.MISSING.alt}`
          ),
        };
    }
  };

  /**
   * 運転モードアイコン
   * @param {apiIF.res.command['operation-mode']} modeState  対象ロジックの運転モード
   * @returns アイコン画像
   */
  const modeIcon = (modeState: apiIF.res.command['operation-mode']) => {
    switch (modeState) {
      case 'auto':
        return {
          src: iconWhite.AUTO.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.AUTO.alt}`
          ),
          class:
            'logic__list__body__row__data__action__operationModeIcon--auto',
        };
      case 'dehumidify':
        return {
          src: iconWhite.DRYING.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.DRYING.alt}`
          ),
          class: 'logic__list__body__row__data__action__operationModeIcon--dry',
        };
      case 'cooling':
        return {
          src: iconWhite.COOLING.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.COOLING.alt}`
          ),
          class:
            'logic__list__body__row__data__action__operationModeIcon--cooling',
        };
      case 'fan':
        return {
          src: iconWhite.BLOWER.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.BLOWER.alt}`
          ),
          class: 'logic__list__body__row__data__action__operationModeIcon--fan',
        };
      case 'heating':
        return {
          src: iconWhite.HEATING.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.HEATING.alt}`
          ),
          class:
            'logic__list__body__row__data__action__operationModeIcon--heating',
        };
      default:
        return {
          src: iconBlack.MISSING.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconBlack.MISSING.alt}`
          ),
          class: 'logic__list__body__row__data__action__operationModeIcon--non',
        };
    }
  };

  /**
   * 風量アイコン
   * @param {apiIF.res.command['air-flow']} modeState  対象ロジックの風量
   * @returns アイコン画像
   */
  const fanSpeedIcon = (modeState: apiIF.res.command['air-flow']) => {
    switch (modeState) {
      case 'auto':
        return {
          src: iconWhite.FAN_SPEED_AUTO.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.FAN_SPEED_AUTO.alt}`
          ),
        };
      case 'low':
        return {
          src: iconWhite.FAN_SPEED_1.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.FAN_SPEED_1.alt}`
          ),
        };
      case 'middle':
        return {
          src: iconWhite.FAN_SPEED_2.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.FAN_SPEED_2.alt}`
          ),
        };
      case 'high':
        return {
          src: iconWhite.FAN_SPEED_3.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.FAN_SPEED_3.alt}`
          ),
        };
      case 'powerful':
        return {
          src: iconWhite.FAN_SPEED_4.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconWhite.FAN_SPEED_4.alt}`
          ),
        };
      default:
        return {
          src: iconBlack.MISSING.src,
          alt: trans(
            `LogicActionControlIconContents.image.alternativeTxt.${iconBlack.MISSING.alt}`
          ),
        };
    }
  };

  return (
    <Grid container spacing={1} alignItems="center">
      {/** 運転/停止 */}
      <Grid item xs={3}>
        {'run-stop' in props.logicInfo['action'] &&
        props.logicInfo['action']['run-stop'] !== '' ? (
          <Box className="logic__list__body__row__data__action__runStopIcon">
            <img
              src={runStopIcon(props.logicInfo['action']['run-stop']).src}
              title={runStopIcon(props.logicInfo['action']['run-stop']).alt}
              alt={runStopIcon(props.logicInfo['action']['run-stop']).alt}
              className="logic__list__body__row__data__action__runStopIcon__image"
            />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
      {/** 運転モード */}
      <Grid item xs={3}>
        {'operation-mode' in props.logicInfo['action'] &&
        props.logicInfo['action']['operation-mode'] !== '' ? (
          <Box
            className={
              modeIcon(props.logicInfo['action']['operation-mode']).class
            }>
            <img
              src={modeIcon(props.logicInfo['action']['operation-mode']).src}
              alt={modeIcon(props.logicInfo['action']['operation-mode']).alt}
              title={modeIcon(props.logicInfo['action']['operation-mode']).alt}
              className="logic__list__body__row__data__action__operationModeIcon__image"
            />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
      {/** 風量 */}
      <Grid item xs={3}>
        {'air-flow' in props.logicInfo['action'] &&
        props.logicInfo['action']['air-flow'] !== '' ? (
          <Box className="logic__list__body__row__data__action__fanSpeedIcon">
            <img
              src={fanSpeedIcon(props.logicInfo['action']['air-flow']).src}
              alt={fanSpeedIcon(props.logicInfo['action']['air-flow']).alt}
              title={fanSpeedIcon(props.logicInfo['action']['air-flow']).alt}
              className="logic__list__body__row__data__action__fanSpeedIcon__image"
            />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
      {/** 設定温度 */}
      <Grid item xs={3}>
        {'preset-temperature' in props.logicInfo['action'] &&
        props.logicInfo['action']['preset-temperature'] !== '' ? (
          `${props.logicInfo['action']['preset-temperature']} °C`
        ) : (
          <></>
        )}
      </Grid>
    </Grid>
  );
};
export default LogicActionControlContents;
